/**
 * Product Flavor Selection
 * クリーンアーキテクチャに基づいたセット商品フレーバー選択機能
 * Vanilla JavaScript実装
 *
 * アーキテクチャ構成:
 * - Domain Layer: ビジネスロジック（セット数からフレーバー数を決定）
 * - Use Case Layer: ユースケース（フレーバー選択、リセット、自動選択）
 * - Interface Adapter Layer: DOM操作、イベントハンドリング
 * - Framework Layer: Vanilla JavaScript、イベントリスナー
 */

(function(window) {
  'use strict';

  // ===============================================
  // Domain Layer - ビジネスロジック
  // ===============================================

  /**
   * セット数のドメインモデル
   */
  const SetQuantity = {
    /**
     * セット数からフレーバー選択数を取得
     * @param {string} setLabel - セットのラベル（例: "3袋【300円OFF+送料無料】"）
     * @returns {number} - 必要なフレーバー選択数
     */
    getFlavorCount: function(setLabel) {
      if (!setLabel) return 1;

      // ラベルから数値を抽出（例: "3袋" → 3）
      const match = setLabel.match(/^(\d+)/);
      if (match && match[1]) {
        const count = parseInt(match[1], 10);
        return isNaN(count) ? 1 : count;
      }

      return 1; // デフォルトは1つ
    },

    /**
     * フレーバー選択が必要な範囲を取得
     * @param {number} count - セット数
     * @returns {Array<number>} - 表示すべきフレーバーセットのインデックス配列
     */
    getRequiredFlavorSets: function(count) {
      const sets = [];
      // 2つ目から指定された数まで
      for (let i = 2; i <= count; i++) {
        sets.push(i);
      }
      return sets;
    },

    /**
     * 非表示にすべきフレーバーセットを取得
     * @param {number} count - 現在のセット数
     * @param {number} maxCount - 最大セット数（デフォルト12）
     * @returns {Array<number>} - 非表示にすべきフレーバーセットのインデックス配列
     */
    getHiddenFlavorSets: function(count, maxCount = 12) {
      const sets = [];
      for (let i = count + 1; i <= maxCount; i++) {
        sets.push(i);
      }
      return sets;
    }
  };

  /**
   * フレーバー選択のドメインモデル
   */
  const FlavorSelection = {
    /**
     * 有効な（選択可能な）フレーバーのみをフィルタリング
     * @param {NodeList|Array} flavorItems - フレーバーアイテムの配列
     * @returns {Array<HTMLElement>} - 有効なフレーバーアイテム
     */
    getAvailableFlavors: function(flavorItems) {
      return Array.from(flavorItems).filter(item =>
        !item.classList.contains('flavor-swatch--disabled')
      );
    },

    /**
     * フレーバーが全て売り切れかチェック
     * @param {NodeList|Array} flavorItems - フレーバーアイテムの配列
     * @returns {boolean}
     */
    isAllSoldOut: function(flavorItems) {
      return this.getAvailableFlavors(flavorItems).length === 0;
    }
  };

  /**
   * バリアント在庫管理のドメインモデル
   */
  const VariantInventory = {
    /**
     * バリアント情報を取得（ページ内のJSONから）
     * @returns {Array<Object>} - バリアント配列
     */
    getVariants: function() {
      const variantScript = document.querySelector('variant-radios script[type="application/json"]');
      if (variantScript) {
        try {
          return JSON.parse(variantScript.textContent);
        } catch (e) {
          console.error('[VariantInventory] Failed to parse variants JSON:', e);
          return [];
        }
      }
      return [];
    },

    /**
     * 特定のセット数×フレーバーの組み合わせが在庫ありかチェック
     * @param {string} setLabel - セット数のラベル（例: "6袋"）
     * @param {string} flavorName - フレーバー名（例: "ココア"）
     * @returns {boolean} - true: 在庫あり, false: 売り切れ
     */
    isAvailable: function(setLabel, flavorName) {
      const variants = this.getVariants();

      // セット数とフレーバーの両方を含むバリアントを検索
      const matchedVariant = variants.find(function(variant) {
        return variant.title.indexOf(setLabel) !== -1 &&
               variant.title.indexOf(flavorName) !== -1;
      });

      if (matchedVariant) {
        return matchedVariant.available;
      }

      // バリアントが見つからない場合は在庫ありとみなす
      return true;
    }
  };

  // ===============================================
  // Use Case Layer - ユースケース
  // ===============================================

  /**
   * フレーバーセット表示ユースケース
   */
  const ShowFlavorSetsUseCase = {
    /**
     * 指定された数のフレーバーセットを表示
     * @param {number} count - 表示するセット数
     * @param {Object} repository - フレーバーセットのリポジトリ
     */
    execute: function(count, repository) {
      // 表示すべきセットを取得
      const requiredSets = SetQuantity.getRequiredFlavorSets(count);
      const hiddenSets = SetQuantity.getHiddenFlavorSets(count);

      // 表示
      requiredSets.forEach(setIndex => {
        repository.show(setIndex);
      });

      // 非表示
      hiddenSets.forEach(setIndex => {
        repository.hide(setIndex);
      });

      console.log(`[ShowFlavorSets] Displaying ${count} sets (2-${count})`);
    }
  };

  /**
   * フレーバー自動選択ユースケース
   */
  const AutoSelectFlavorUseCase = {
    /**
     * フレーバーセット内の最初の有効なフレーバーを自動選択
     * @param {number} setIndex - フレーバーセットのインデックス
     * @param {Object} repository - フレーバーセットのリポジトリ
     */
    execute: function(setIndex, repository) {
      const flavorSet = repository.getFlavorSet(setIndex);
      if (!flavorSet || !this._isVisible(flavorSet)) {
        return;
      }

      const flavorItems = repository.getFlavorItems(flavorSet);
      const availableItems = FlavorSelection.getAvailableFlavors(flavorItems);

      if (availableItems.length > 0) {
        // 最初の有効なフレーバーを選択
        const firstRadio = availableItems[0].querySelector('input[type="radio"]');
        if (firstRadio) {
          firstRadio.checked = true;
          console.log(`[AutoSelect] Set ${setIndex}: Selected first available flavor`);
        }
      } else {
        // 全て売り切れの場合
        this._handleAllSoldOut(flavorSet);
      }
    },

    _isVisible: function(element) {
      return !element.classList.contains('flavor-selector-set--hidden') &&
             element.offsetParent !== null;
    },

    _handleAllSoldOut: function(flavorSet) {
      const grid = flavorSet.querySelector('.flavor-swatch-list');
      if (grid && !grid.querySelector('.all-flavors-sold-out')) {
        const message = document.createElement('div');
        message.className = 'all-flavors-sold-out';
        message.style.cssText = 'text-align: center; padding: 15px; color: #999;';
        message.textContent = '申し訳ございません。現在全てのフレーバーが売り切れです。';
        grid.appendChild(message);
      }
    }
  };

  /**
   * フレーバー選択リセットユースケース
   */
  const ResetFlavorSelectionsUseCase = {
    /**
     * 指定されたセット以降のフレーバー選択をリセット
     * @param {number} fromSet - この番号以降のセットをリセット
     * @param {Object} repository - フレーバーセットのリポジトリ
     */
    execute: function(fromSet, repository) {
      for (let i = fromSet; i <= 12; i++) {
        const flavorSet = repository.getFlavorSet(i);
        if (flavorSet) {
          const radios = flavorSet.querySelectorAll('input[type="radio"]');
          radios.forEach(radio => {
            radio.checked = false;
          });
          console.log(`[Reset] Set ${i}: Unchecked all flavors`);
        }
      }
    }
  };

  /**
   * フレーバー在庫状態更新ユースケース
   */
  const UpdateFlavorInventoryUseCase = {
    /**
     * 選択されたセット数に基づいてフレーバーの在庫状態を更新
     * @param {string} setLabel - セット数のラベル（例: "6袋"）
     * @param {Object} repository - フレーバーセットのリポジトリ
     */
    execute: function(setLabel, repository) {
      console.log(`[UpdateInventory] Updating inventory for: ${setLabel}`);

      // メインのフレーバー選択UI（1つ目）も更新
      this._updateMainFlavorSelector(setLabel);

      // 全てのフレーバーセット（2-12）に対して在庫チェック
      for (let i = 2; i <= 12; i++) {
        const flavorSet = repository.getFlavorSet(i);
        if (!flavorSet) continue;

        const flavorItems = flavorSet.querySelectorAll('.flavor-swatch');

        flavorItems.forEach(function(flavorItem) {
          const input = flavorItem.querySelector('input[type="radio"]');
          if (!input) return;

          const flavorName = input.value;
          const isAvailable = VariantInventory.isAvailable(setLabel, flavorName);

          if (isAvailable) {
            // 在庫あり: 有効化
            flavorItem.classList.remove('flavor-swatch--disabled');
            flavorItem.setAttribute('data-available', 'true');
            input.disabled = false;
          } else {
            // 売り切れ: 無効化
            flavorItem.classList.add('flavor-swatch--disabled');
            flavorItem.setAttribute('data-available', 'false');
            input.disabled = true;
            // 選択状態を解除
            input.checked = false;
          }
        });
      }
    },

    /**
     * メインのフレーバー選択UI（1つ目）の在庫状態を更新
     * @param {string} setLabel - セット数のラベル
     */
    _updateMainFlavorSelector: function(setLabel) {
      const mainFlavorSelector = document.querySelector('.product-form__input--flavor');
      if (!mainFlavorSelector) return;

      const flavorItems = mainFlavorSelector.querySelectorAll('.flavor-swatch');

      flavorItems.forEach(function(flavorItem) {
        const input = flavorItem.querySelector('input[type="radio"]');
        if (!input) return;

        const flavorName = input.value;
        const isAvailable = VariantInventory.isAvailable(setLabel, flavorName);

        if (isAvailable) {
          // 在庫あり: 有効化
          flavorItem.classList.remove('flavor-swatch--disabled');
          input.disabled = false;
          input.classList.remove('disabled');
        } else {
          // 売り切れ: 無効化
          flavorItem.classList.add('flavor-swatch--disabled');
          input.disabled = true;
          input.classList.add('disabled');
          // 選択状態を解除
          if (input.checked) {
            input.checked = false;
            // 最初の有効なフレーバーを選択
            const firstAvailable = mainFlavorSelector.querySelector('.flavor-swatch:not(.flavor-swatch--disabled) input[type="radio"]');
            if (firstAvailable) {
              firstAvailable.checked = true;
            }
          }
        }
      });
    }
  };

  // ===============================================
  // Interface Adapter Layer - リポジトリ
  // ===============================================

  /**
   * フレーバーセットのリポジトリ（DOM操作の抽象化）
   */
  const FlavorSetRepository = {
    /**
     * フレーバーセットのDOM要素を取得
     * @param {number} setIndex - セットのインデックス
     * @returns {HTMLElement|null}
     */
    getFlavorSet: function(setIndex) {
      return document.querySelector(`.flavor-selector-set--${setIndex}`);
    },

    /**
     * フレーバーセット内のフレーバーアイテムを取得
     * @param {HTMLElement} flavorSet - フレーバーセットの要素
     * @returns {NodeList}
     */
    getFlavorItems: function(flavorSet) {
      return flavorSet.querySelectorAll('.flavor-swatch');
    },

    /**
     * フレーバーセットを表示
     * @param {number} setIndex - セットのインデックス
     */
    show: function(setIndex) {
      const element = this.getFlavorSet(setIndex);
      if (element) {
        element.classList.remove('flavor-selector-set--hidden');
      }
    },

    /**
     * フレーバーセットを非表示
     * @param {number} setIndex - セットのインデックス
     */
    hide: function(setIndex) {
      const element = this.getFlavorSet(setIndex);
      if (element) {
        element.classList.add('flavor-selector-set--hidden');
      }
    }
  };

  /**
   * セット選択のリポジトリ
   */
  const SetSelectionRepository = {
    /**
     * 現在選択されているセットのラベルを取得
     * @returns {string}
     */
    getSelectedSetLabel: function() {
      // 日本語の"セット数"とoption1の両方に対応
      const selectedInput = document.querySelector('input[name="セット数"]:checked, input[name="option1"]:checked');
      if (selectedInput) {
        // valueから直接取得（例: "3袋" → "3袋"）
        return selectedInput.value || '';
      }
      return '';
    },

    /**
     * セット選択の変更イベントを監視
     * @param {Function} callback - 変更時のコールバック
     */
    onSetChange: function(callback) {
      document.addEventListener('change', function(event) {
        if (event.target.matches('input[name="セット数"], input[name="option1"]')) {
          callback(event);
        }
      });
    }
  };

  // ===============================================
  // Framework Layer - コントローラー
  // ===============================================

  /**
   * フレーバーセット表示コントローラー
   */
  const FlavorSetController = {
    currentUrl: location.href,

    /**
     * 初期化
     */
    initialize: function() {
      this._setupSetChangeHandler();
      this._setupUrlChangeMonitor();
      this._setupFlavorChangeHandler();
      this._initializeOnLoad();

      console.log('[FlavorSetController] Initialized');
    },

    /**
     * フレーバー選択変更時のハンドラー（フォーム状態更新）
     */
    _setupFlavorChangeHandler: function() {
      document.addEventListener('change', function(event) {
        if (event.target.matches('.flavor-swatch input[type="radio"]')) {
          console.log('[Controller] Flavor changed, triggering form update');

          // Shopifyのvariant選択システムにイベントを通知
          const form = event.target.form;
          if (form) {
            // フォームの変更イベントをトリガー
            const changeEvent = new Event('change', { bubbles: true });
            form.dispatchEvent(changeEvent);
          }

          // submit buttonの状態を確認・更新
          setTimeout(function() {
            const submitButton = document.querySelector('[name="add"]');
            if (submitButton) {
              // 全ての必須フレーバーが選択されているかチェック
              const visibleSets = document.querySelectorAll('.flavor-selector-set:not(.flavor-selector-set--hidden)');
              let allSelected = true;

              visibleSets.forEach(function(set) {
                const selectedRadio = set.querySelector('.flavor-swatch input[type="radio"]:checked');
                if (!selectedRadio) {
                  allSelected = false;
                }
              });

              // メインのフレーバー選択もチェック
              const mainFlavorSelector = document.querySelector('.product-form__input--flavor');
              if (mainFlavorSelector) {
                const mainSelected = mainFlavorSelector.querySelector('.flavor-swatch input[type="radio"]:checked');
                if (!mainSelected) {
                  allSelected = false;
                }
              }

              if (allSelected) {
                submitButton.disabled = false;
                submitButton.querySelector('span').textContent = 'カートに追加する';
              }
            }
          }, 100);
        }
      });
    },

    /**
     * セット選択変更時のハンドラー設定
     */
    _setupSetChangeHandler: function() {
      SetSelectionRepository.onSetChange(function() {
        const setLabel = SetSelectionRepository.getSelectedSetLabel();
        const flavorCount = SetQuantity.getFlavorCount(setLabel);

        console.log(`[Controller] Set changed: "${setLabel}" → ${flavorCount} flavors`);

        // フレーバーの在庫状態を更新
        UpdateFlavorInventoryUseCase.execute(setLabel, FlavorSetRepository);

        // 現在のセット数より多いフレーバー選択をリセット
        ResetFlavorSelectionsUseCase.execute(flavorCount + 1, FlavorSetRepository);

        // 必要なフレーバーセットを表示
        ShowFlavorSetsUseCase.execute(flavorCount, FlavorSetRepository);

        // 表示されたフレーバーセットで自動選択
        setTimeout(function() {
          const requiredSets = SetQuantity.getRequiredFlavorSets(flavorCount);
          requiredSets.forEach(function(setIndex) {
            AutoSelectFlavorUseCase.execute(setIndex, FlavorSetRepository);
          });
        }, 300);
      });
    },

    /**
     * URL変更監視（バリアント変更検知）
     */
    _setupUrlChangeMonitor: function() {
      const self = this;

      SetSelectionRepository.onSetChange(function() {
        setTimeout(function() {
          if (self.currentUrl !== location.href) {
            console.log('[Controller] URL changed detected');

            // 全てのフレーバー選択をリセット
            for (let i = 2; i <= 12; i++) {
              const flavorSet = FlavorSetRepository.getFlavorSet(i);
              if (flavorSet && !flavorSet.classList.contains('flavor-selector-set--hidden')) {
                const radios = flavorSet.querySelectorAll('input[type="radio"]');
                radios.forEach(radio => {
                  radio.checked = false;
                });
              }
            }

            self.currentUrl = location.href;

            // 再度自動選択
            const setLabel = SetSelectionRepository.getSelectedSetLabel();
            const flavorCount = SetQuantity.getFlavorCount(setLabel);
            const requiredSets = SetQuantity.getRequiredFlavorSets(flavorCount);

            requiredSets.forEach(function(setIndex) {
              AutoSelectFlavorUseCase.execute(setIndex, FlavorSetRepository);
            });
          }
        }, 400);
      });
    },

    /**
     * ページ読み込み時の初期化
     */
    _initializeOnLoad: function() {
      // 初期状態のセット数を確認
      const setLabel = SetSelectionRepository.getSelectedSetLabel();
      const flavorCount = SetQuantity.getFlavorCount(setLabel);

      // 初期読み込み時にフレーバーの在庫状態を更新
      if (setLabel) {
        UpdateFlavorInventoryUseCase.execute(setLabel, FlavorSetRepository);
      }

      if (flavorCount > 1) {
        console.log(`[Controller] Initial load: ${flavorCount} sets detected`);
        ShowFlavorSetsUseCase.execute(flavorCount, FlavorSetRepository);

        setTimeout(function() {
          const requiredSets = SetQuantity.getRequiredFlavorSets(flavorCount);
          requiredSets.forEach(function(setIndex) {
            AutoSelectFlavorUseCase.execute(setIndex, FlavorSetRepository);
          });
        }, 300);
      }
    }
  };

  // ===============================================
  // アプリケーションエントリーポイント
  // ===============================================

  /**
   * 初期化
   */
  function initialize() {
    console.log('[Product Flavor Selection] Initializing with Clean Architecture (Vanilla JS)...');
    FlavorSetController.initialize();
    console.log('[Product Flavor Selection] Ready.');
  }

  // DOM読み込み完了後に初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    // すでに読み込み完了している場合は即座に実行
    initialize();
  }

  // グローバルに公開（デバッグ用）
  window.ProductFlavorSelection = {
    Domain: {
      SetQuantity: SetQuantity,
      FlavorSelection: FlavorSelection,
      VariantInventory: VariantInventory
    },
    UseCase: {
      ShowFlavorSets: ShowFlavorSetsUseCase,
      AutoSelectFlavor: AutoSelectFlavorUseCase,
      ResetFlavorSelections: ResetFlavorSelectionsUseCase,
      UpdateFlavorInventory: UpdateFlavorInventoryUseCase
    },
    Repository: {
      FlavorSet: FlavorSetRepository,
      SetSelection: SetSelectionRepository
    },
    Controller: FlavorSetController
  };

})(window);
